import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ImageDataLike } from "gatsby-plugin-image";
import { LinkType } from "../../types";
import Typography from "../typography/Typography";
import Stack from "../stack/Stack";
import { useBackgroundColor } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Link from "../link/Link";
import Image from "../image/Image";
import ContainerGrid from "../containerGrid/ContainerGrid";
import Html from "../html/Html";

// Interface

export type FeaturesDisplayOptions =
  | "featuredOnLeft"
  | "featuredOnRight"
  | "threeColumns";

export interface FeaturesProps {
  features: FeatureCard[];
  display: FeaturesDisplayOptions;
}

export interface FeatureCard {
  feature: Feature;
  backgroundColour: ThemeColorsKeys;
  overrideImage?: ImageDataLike | string;
}

export interface Feature {
  heading: string;
  subHeading: string;
  link: LinkType;
  image?: ImageDataLike | string;
}

export interface StyledFeatureProps {
  backgroundColour: ThemeColorsKeys;
  hasLink?: boolean;
}

interface StyledBodyprops {
  noImage: boolean;
}

interface StyledTextProps {
  isFeatured: boolean;
}

// Styles

const StyledFeature = styled.div<StyledFeatureProps>`
  ${({
    theme: { space, colors, media, borderRadii },
    backgroundColour,
    hasLink,
  }) => css`
    position: relative;
    ${useBackgroundColor(backgroundColour)}
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${hasLink &&
    css`
      cursor: pointer;
    `}

    a {
      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  `};
`;

const StyledBody = styled(Stack)<StyledBodyprops>`
  ${({ theme: { space }, noImage }) => css`
    padding: ${space.fluidMediumToLarge} ${space.fluidMediumToLarge}
      ${noImage ? space.fluidMediumToLarge : "0"} ${space.fluidMediumToLarge};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  `};
`;

const StyledHeading = styled(Typography)`
  ${({ theme: { media } }) => css`
    /* flex-grow: 1; */
  `};
`;

const StyledText = styled(Html)<StyledTextProps>`
  ${({ theme: { space }, isFeatured }) => css`
    ${!isFeatured &&
    css`
      flex-grow: 1;
    `}
  `};
`;

const StyledImage = styled.div`
  ${({ theme: { space } }) => css`
    padding: 0 ${space.fluidMediumToLarge} ${space.fluidMediumToLarge}
      ${space.fluidMediumToLarge};
    margin-top: ${space.xxSmall};

    > * {
      margin-left: auto;
      max-width: 130px;
      display: flex;
      justify-content: flex-end;
    }
  `};
`;

const StyledOverrideImage = styled.div`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.xxSmall};

    > * {
      margin-left: auto;
      max-width: 194px;
    }

    @media (min-width: ${media.medium}) {
      > * {
        max-width: 487px;
      }
    }
  `};
`;

// JSX

const FeatureCard: FC<FeatureCard> = ({
  feature: { heading, subHeading, link, image },
  backgroundColour = "white",
  overrideImage,
}) => (
  <StyledFeature backgroundColour={backgroundColour} hasLink={!!link}>
    <StyledBody gap="xSmall" noImage={!image && !overrideImage}>
      {heading && (
        <StyledHeading
          component="h3"
          variant={subHeading ? "headingXSmall" : "headingLarge"}
        >
          {heading}
        </StyledHeading>
      )}
      {subHeading && (
        <StyledText isFeatured={overrideImage ? true : false}>
          {subHeading}
        </StyledText>
      )}
      {link && (
        <Link {...link} dataLayerClickContext={`Feature cards - ${heading}`} />
      )}
    </StyledBody>
    {overrideImage && (
      <StyledOverrideImage>
        <Image src={overrideImage} alt={heading} />
      </StyledOverrideImage>
    )}
    {!overrideImage && image && (
      <StyledImage>
        <Image src={image} alt={heading} />
      </StyledImage>
    )}
  </StyledFeature>
);

export const Features: FC<FeaturesProps> = ({ features, display }) => (
  <ContainerGrid numberOfItems={features?.length} display={display}>
    {features.map((feature, index) => (
      <FeatureCard key={index} {...feature} />
    ))}
  </ContainerGrid>
);

export default Features;
